import { mapApiItemToReminderItem, type ReminderApiItem, type ReminderItem } from '../../hooks/reminder-mapping'
import type { MeetingSummaryPhase } from './derive-meeting-summary-state'

/**
 * Shaping the "For You" list, kept apart from `use-meeting-follow-ups` so it
 * stays free of the auth and API-route imports the fetch needs — those reach
 * into Electron's `app` and can't be loaded in a unit test.
 */

export interface MeetingFollowUp {
  kind: 'reminder'
  id: string
  createdAt: number
  isOpen: boolean
  reminder: ReminderItem
}

export interface MeetingFollowUps {
  items: Array<MeetingFollowUp>
  openCount: number
}

export const NO_FOLLOW_UPS: MeetingFollowUps = { items: [], openCount: 0 }

interface MeetingPlannerJob {
  sourceId: string | null
  status: string
}

interface MeetingPlannerSources {
  audioNoteId?: string | null
  audioSessionId?: string | null
}

export function isMeetingPlannerRunning(
  jobs: Array<MeetingPlannerJob>,
  { audioNoteId, audioSessionId }: MeetingPlannerSources,
): boolean {
  const sourceIds = new Set([audioNoteId, audioSessionId].filter((id): id is string => !!id))
  return jobs.some(
    (job) =>
      job.sourceId !== null && sourceIds.has(job.sourceId) && (job.status === 'queued' || job.status === 'generating'),
  )
}

/** Summary phase alone cannot tell whether the user's plan and storage mode allow drafts. */
export function resolveFollowUpsEmptyCopy(phase: MeetingSummaryPhase, canDraftFollowUps: boolean): string | undefined {
  if (!canDraftFollowUps) {
    return undefined
  }

  switch (phase) {
    case 'preparing':
    case 'streaming':
      return 'Preparing follow-ups while the summary is being written…'
    case 'finalized':
      return 'You can chat about this meeting to create some'
    case 'failed':
      return "The summary couldn't be generated. You can chat about this meeting to create follow-ups."
    case 'missing':
      return 'No summary is available for this meeting. You can chat about it to create follow-ups.'
    case 'live':
    case 'unavailable':
      return undefined
  }
}

/** Ascending so new arrivals append without moving content the user is reading. */
export function compareFollowUps(a: MeetingFollowUp, b: MeetingFollowUp): number {
  if (a.createdAt !== b.createdAt) {
    return a.createdAt - b.createdAt
  }
  return a.id.localeCompare(b.id)
}

/**
 * The server already drops deleted/archived rows; the only filter left here is
 * dismissed reminders, which stay resolvable through the panel and so come back
 * on the wire. Resolved reminders remain in the meeting record with a strikethrough.
 */
export function buildMeetingFollowUps(reminders: Array<unknown> | undefined): MeetingFollowUps {
  if (!reminders?.length) {
    return NO_FOLLOW_UPS
  }

  const items: Array<MeetingFollowUp> = []

  for (const apiItem of (reminders ?? []) as Array<ReminderApiItem>) {
    const reminder = mapApiItemToReminderItem(apiItem)
    if (reminder.state === 'dismissed') {
      continue
    }
    items.push({
      kind: 'reminder',
      id: reminder.id,
      createdAt: reminder.createdAt.getTime(),
      isOpen: reminder.resolved !== true,
      reminder,
    })
  }

  items.sort(compareFollowUps)
  return { items, openCount: items.filter((item) => item.isOpen).length }
}
